import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox, Newspaper, Users, Image as ImageIcon, LogOut, Loader2, Search, Download,
  Plus, Trash2, Pencil, X, UploadCloud, ArrowLeft, RefreshCw, KeyRound, Smartphone, Bell, Trophy,
} from "lucide-react";
import { getToken, getUser, clearSession, authHeaders, formatApiError } from "../lib/admin";
import { usePageTitle } from "../components/Shared";
import ConcoursAdmin from "./admin/ConcoursAdmin";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { FORM_SERVICES, PROFILS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUTS = [
  { key: "recue", label: "Demande reçue" },
  { key: "en_etude", label: "Étude & devis" },
  { key: "documents_recus", label: "Documents vérifiés" },
  { key: "en_traitement", label: "En traitement" },
  { key: "terminee", label: "Terminée" },
];
const STATUT_LABEL = Object.fromEntries(STATUTS.map((s) => [s.key, s.label]));
const STATUT_STYLE = {
  recue: "bg-slate-100 text-slate-600",
  en_etude: "bg-gold-glow text-gold",
  documents_recus: "bg-brand-light text-brand",
  en_traitement: "bg-brand text-white",
  terminee: "bg-gold text-brand-deep",
};

const inputCls =
  "w-full rounded-xl border border-brand/15 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

/* ---------------- Demandes ---------------- */
const SOURCES = { site: "Site web", whatsapp: "WhatsApp", telephone: "Téléphone", email: "Email", autre: "Autre" };

const NouvelleDemandeForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", pays: "", profil: "", categorie: "", service: "", source: "whatsapp", message: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value, ...(k === "categorie" ? { service: "" } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/demandes`, form, { headers: authHeaders() });
      toast.success(`Demande ${data.reference} créée — visible dans la liste.`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-brand-deep/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <form onSubmit={submit} data-testid="demande-manuelle-form" className="my-8 w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900">Nouvelle demande manuelle</h3>
            <p className="mt-1 text-sm text-slate-500">Pour une demande reçue par WhatsApp, téléphone ou email.</p>
          </div>
          <button type="button" onClick={onClose} data-testid="demande-manuelle-close" className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Nom complet *</label>
            <input required minLength={2} value={form.nom} onChange={set("nom")} data-testid="dm-nom" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Téléphone / WhatsApp *</label>
            <input required minLength={6} value={form.telephone} onChange={set("telephone")} data-testid="dm-telephone" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email (optionnel)</label>
            <input type="email" value={form.email} onChange={set("email")} data-testid="dm-email" className={inputCls} placeholder="Si fourni, le client reçoit un email" />
          </div>
          <div>
            <label className={labelCls}>Pays de résidence</label>
            <input value={form.pays} onChange={set("pays")} data-testid="dm-pays" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Profil</label>
            <select value={form.profil} onChange={set("profil")} data-testid="dm-profil" className={inputCls}>
              <option value="">—</option>
              {PROFILS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Source *</label>
            <select value={form.source} onChange={set("source")} data-testid="dm-source" className={inputCls}>
              <option value="whatsapp">WhatsApp</option>
              <option value="telephone">Téléphone</option>
              <option value="email">Email</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Catégorie</label>
            <select value={form.categorie} onChange={set("categorie")} data-testid="dm-categorie" className={inputCls}>
              <option value="">Autre besoin</option>
              {Object.keys(FORM_SERVICES).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Démarche</label>
            <select value={form.service} onChange={set("service")} data-testid="dm-service" className={inputCls} disabled={!form.categorie || !FORM_SERVICES[form.categorie]}>
              <option value="">—</option>
              {form.categorie && FORM_SERVICES[form.categorie] && FORM_SERVICES[form.categorie].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Notes / message du client</label>
            <textarea rows={3} value={form.message} onChange={set("message")} data-testid="dm-message" className={`${inputCls} resize-y`} />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="dm-submit" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Créer la demande
        </button>
      </form>
    </div>
  );
};

const DemandesTab = () => {
  const [demandes, setDemandes] = useState(null);
  const [statut, setStatut] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [savingStatut, setSavingStatut] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [commentaire, setCommentaire] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statut) params.statut = statut;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/admin/demandes`, { headers: authHeaders(), params });
      setDemandes(data);
    } catch {
      toast.error("Impossible de charger les demandes");
    }
  }, [statut, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/auth/users`, { headers: authHeaders() })
      .then((r) => setCollaborateurs(r.data.filter((u) => u.actif)))
      .catch(() => {});
  }, []);

  const openDetail = async (ref) => {
    try {
      const { data } = await axios.get(`${API}/admin/demandes/${ref}`, { headers: authHeaders() });
      setDetail(data);
      setCommentaire("");
    } catch {
      toast.error("Erreur de chargement du dossier");
    }
  };

  const downloadFile = async (ref, index, name) => {
    try {
      const res = await axios.get(`${API}/admin/demandes/${ref}/fichiers/${index}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const changeStatut = async (newStatut) => {
    setSavingStatut(true);
    try {
      await axios.put(`${API}/suivi/${detail.reference}/statut`, { statut: newStatut }, { headers: authHeaders() });
      setDetail({ ...detail, statut: newStatut });
      toast.success(detail.email ? "Statut mis à jour — client notifié par email" : "Statut mis à jour");
      load();
    } catch {
      toast.error("Échec de la mise à jour");
    } finally {
      setSavingStatut(false);
    }
  };

  const changeAssignation = async (userId) => {
    try {
      const { data } = await axios.put(
        `${API}/admin/demandes/${detail.reference}/assignation`,
        { assigne_a: userId || null },
        { headers: authHeaders() }
      );
      setDetail({ ...detail, assigne_a: data.assigne_a, assigne_nom: data.assigne_nom });
      toast.success(data.assigne_nom ? `Dossier assigné à ${data.assigne_nom}` : "Assignation retirée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addCommentaire = async (e) => {
    e.preventDefault();
    if (!commentaire.trim()) return;
    setSendingComment(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/demandes/${detail.reference}/commentaires`,
        { texte: commentaire },
        { headers: authHeaders() }
      );
      setDetail({ ...detail, commentaires: [...(detail.commentaires || []), data] });
      setCommentaire("");
    } catch {
      toast.error("Impossible d'ajouter le commentaire");
    } finally {
      setSendingComment(false);
    }
  };

  return (
    <div data-testid="admin-demandes-tab">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="demandes-search"
            placeholder="Rechercher (référence, nom, email)…"
            className={`${inputCls} pl-11`}
          />
        </div>
        <select value={statut} onChange={(e) => setStatut(e.target.value)} data-testid="demandes-filter-statut" className={`${inputCls} lg:w-52`}>
          <option value="">Tous les statuts</option>
          {STATUTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={load} data-testid="demandes-refresh" className="inline-flex items-center justify-center gap-2 rounded-xl border border-brand/15 bg-white px-4 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand-light">
          <RefreshCw className="h-4 w-4" /> Actualiser
        </button>
        <button onClick={() => setShowNew(true)} data-testid="demande-new-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> Nouvelle demande
        </button>
      </div>

      {demandes === null ? (
        <div className="py-16 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : demandes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand/20 bg-white p-12 text-center text-sm text-slate-500">
          Aucune demande pour ces critères.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-brand/10 bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="demandes-table">
            <thead>
              <tr className="border-b border-brand/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-5 py-4 font-bold">Référence</th>
                <th className="px-5 py-4 font-bold">Client</th>
                <th className="px-5 py-4 font-bold">Catégorie</th>
                <th className="px-5 py-4 font-bold">Source</th>
                <th className="px-5 py-4 font-bold">Assigné à</th>
                <th className="px-5 py-4 font-bold">Statut</th>
                <th className="px-5 py-4 font-bold">Date</th>
              </tr>
            </thead>
            <tbody>
              {demandes.map((d) => (
                <tr
                  key={d.reference}
                  onClick={() => openDetail(d.reference)}
                  data-testid={`demande-row-${d.reference}`}
                  className="cursor-pointer border-b border-slate-50 transition-colors last:border-0 hover:bg-brand-light/40"
                >
                  <td className="px-5 py-4 font-mono text-xs font-bold text-brand">{d.reference}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-slate-900">{d.nom}</div>
                    <div className="text-xs text-slate-400">{d.email || d.telephone}</div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{d.categorie}</td>
                  <td className="px-5 py-4">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {SOURCES[d.source] || "Site web"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-600">{d.assigne_nom || <span className="text-slate-300">—</span>}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUT_STYLE[d.statut] || STATUT_STYLE.recue}`}>
                      {STATUT_LABEL[d.statut] || "Demande reçue"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-400">{fmtDate(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NouvelleDemandeForm onClose={() => setShowNew(false)} onCreated={load} />}

      {detail && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-brand-deep/50 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div
            data-testid="demande-detail"
            className="h-full w-full max-w-lg overflow-y-auto bg-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Dossier</div>
                <div className="mt-1 font-mono text-xl font-bold text-brand">{detail.reference}</div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="demande-detail-close" className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <dl className="mt-7 space-y-4 text-sm">
              {[
                ["Nom", detail.nom],
                ["Email", detail.email || "—"],
                ["Téléphone", detail.telephone],
                ["Pays de résidence", detail.pays],
                ["Profil", detail.profil],
                ["Catégorie", detail.categorie],
                ["Démarche", detail.service || "—"],
                ["Source", SOURCES[detail.source] || "Site web"],
                ["Reçue le", fmtDate(detail.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-50 pb-3">
                  <dt className="shrink-0 text-slate-400">{k}</dt>
                  <dd className="text-right font-medium text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            {detail.message && (
              <div className="mt-6">
                <div className={labelCls}>Message du client</div>
                <p className="rounded-xl bg-ivory p-4 text-sm leading-relaxed text-slate-700">{detail.message}</p>
              </div>
            )}
            <div className="mt-6">
              <div className={labelCls}>Documents joints ({(detail.fichiers || []).length})</div>
              {(detail.fichiers || []).length === 0 ? (
                <p className="text-sm text-slate-400">Aucun document.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.fichiers.map((f, i) => (
                    <li key={i}>
                      <button
                        onClick={() => downloadFile(detail.reference, i, f.nom)}
                        data-testid={`demande-fichier-${i}`}
                        className="flex w-full items-center gap-2.5 rounded-xl border border-brand/10 px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:border-brand hover:bg-brand-light/40"
                      >
                        <Download className="h-4 w-4 shrink-0 text-brand" />
                        <span className="truncate">{f.nom}</span>
                        <span className="ml-auto shrink-0 text-xs text-slate-400">{(f.taille / 1024).toFixed(0)} Ko</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-6">
              <div className={labelCls}>Assigné à</div>
              <select
                value={detail.assigne_a || ""}
                onChange={(e) => changeAssignation(e.target.value)}
                data-testid="demande-assignation-select"
                className={inputCls}
              >
                <option value="">Non assigné</option>
                {collaborateurs.map((u) => <option key={u.id} value={u.id}>{u.nom || u.email}</option>)}
              </select>
            </div>
            <div className="mt-6 rounded-2xl border border-gold/30 bg-gold-glow/40 p-5">
              <div className={labelCls}>Changer le statut</div>
              <select
                value={detail.statut}
                onChange={(e) => changeStatut(e.target.value)}
                disabled={savingStatut}
                data-testid="demande-statut-select"
                className={inputCls}
              >
                {STATUTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <p className="mt-2.5 text-xs text-slate-500">Le client est notifié par email à chaque changement (si email connu).</p>
            </div>
            <div className="mt-6">
              <div className={labelCls}>Notes internes ({(detail.commentaires || []).length})</div>
              <div className="space-y-3" data-testid="demande-commentaires">
                {(detail.commentaires || []).length === 0 && (
                  <p className="text-sm text-slate-400">Aucune note. Consignez ici les échanges et l'avancement interne.</p>
                )}
                {(detail.commentaires || []).map((c, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-ivory p-4">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-brand">{c.auteur}</span>
                      <span className="text-slate-400">{fmtDate(c.date)}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{c.texte}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={addCommentaire} className="mt-4 flex gap-2">
                <input
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  data-testid="demande-commentaire-input"
                  placeholder="Ajouter une note interne…"
                  className={inputCls}
                />
                <button type="submit" disabled={sendingComment || !commentaire.trim()} data-testid="demande-commentaire-btn" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50">
                  {sendingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Blog ---------------- */
const BlogTab = () => {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { headers: authHeaders() });
      setArticles(data);
    } catch {
      toast.error("Impossible de charger les articles");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = async (a) => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/${a.id}`, { headers: authHeaders() });
      setEditing({ ...data, coverFile: null });
    } catch {
      toast.error("Erreur de chargement de l'article");
    }
  };

  const save = async () => {
    if (!editing.titre.trim() || !editing.contenu.trim()) {
      toast.error("Titre et contenu sont requis");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titre", editing.titre);
      fd.append("extrait", editing.extrait || "");
      fd.append("contenu", editing.contenu);
      fd.append("publie", editing.publie ? "true" : "false");
      if (editing.coverFile) fd.append("cover", editing.coverFile);
      if (editing.id) {
        await axios.put(`${API}/admin/blog/${editing.id}`, fd, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/blog`, fd, { headers: authHeaders() });
      }
      toast.success(editing.id ? "Article mis à jour" : "Article créé");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement « ${a.titre} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${a.id}`, { headers: authHeaders() });
      toast.success("Article supprimé");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  if (editing) {
    return (
      <div data-testid="blog-editor" className="rounded-2xl border border-brand/10 bg-white p-7 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">
            {editing.id ? "Modifier l'article" : "Nouvel article"}
          </h3>
          <button onClick={() => setEditing(null)} data-testid="blog-editor-cancel" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Retour à la liste
          </button>
        </div>
        <div className="space-y-5">
          <div>
            <label className={labelCls} htmlFor="blog-titre">Titre *</label>
            <input id="blog-titre" value={editing.titre} onChange={(e) => setEditing({ ...editing, titre: e.target.value })} data-testid="blog-titre" placeholder="Ex : Comment obtenir un passeport togolais depuis l'étranger" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="blog-extrait">Extrait (résumé affiché sur la carte)</label>
            <textarea id="blog-extrait" rows={2} maxLength={400} value={editing.extrait} onChange={(e) => setEditing({ ...editing, extrait: e.target.value })} data-testid="blog-extrait" className={`${inputCls} resize-y`} />
          </div>
          <div>
            <label className={labelCls}>Contenu * — éditeur visuel (gras, titres, listes, liens)</label>
            <div
              data-testid="blog-contenu"
              className="overflow-hidden rounded-xl border border-brand/15 bg-white [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-brand/15 [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[300px] [&_.ql-editor]:text-sm [&_.ql-editor]:leading-relaxed [&_.ql-editor]:text-slate-800"
            >
              <ReactQuill
                theme="snow"
                value={editing.contenu}
                onChange={(html) => setEditing({ ...editing, contenu: html })}
                placeholder="Rédigez votre article…"
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ header: [2, 3, false] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Image de couverture (optionnel)</label>
              <label data-testid="blog-cover-drop" className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-brand/25 bg-brand-light/40 px-4 py-4 text-sm text-slate-600 transition-colors hover:border-brand">
                <UploadCloud className="h-5 w-5 shrink-0 text-brand" />
                <span className="truncate">{editing.coverFile ? editing.coverFile.name : editing.cover_url ? "Image existante — cliquer pour remplacer" : "Choisir une image (PNG, JPG, WebP)"}</span>
                <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" data-testid="blog-cover-input" onChange={(e) => setEditing({ ...editing, coverFile: e.target.files[0] || null })} />
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={editing.publie} onChange={(e) => setEditing({ ...editing, publie: e.target.checked })} data-testid="blog-publie" className="h-4 w-4 rounded border-brand/30 accent-[#0D482B]" />
                Publier immédiatement
              </label>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="blog-save-btn" className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editing.id ? "Enregistrer les modifications" : "Créer l'article"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-tab">
      <div className="mb-6 flex justify-end">
        <button
          onClick={() => setEditing({ id: null, titre: "", extrait: "", contenu: "", publie: true, coverFile: null, cover_url: null })}
          data-testid="blog-new-btn"
          className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-display text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:-translate-y-0.5"
        >
          <Plus className="h-4 w-4" /> Nouvel article
        </button>
      </div>
      {articles === null ? (
        <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div>
      ) : articles.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand/20 bg-white p-12 text-center text-sm text-slate-500">
          Aucun article. Créez votre premier guide !
        </div>
      ) : (
        <div className="space-y-3" data-testid="blog-list">
          {articles.map((a) => (
            <div key={a.id} data-testid={`blog-row-${a.slug}`} className="flex items-center gap-4 rounded-2xl border border-brand/10 bg-white p-4 shadow-sm">
              {a.cover_url ? (
                <img src={`${BACKEND}${a.cover_url}`} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="grid h-14 w-20 shrink-0 place-items-center rounded-lg bg-brand-deep font-display text-[10px] font-bold text-gold">DOC</div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-display font-bold text-slate-900">{a.titre}</div>
                <div className="mt-0.5 text-xs text-slate-400">
                  {fmtDate(a.created_at)} · {a.auteur_nom} ·{" "}
                  <span className={a.publie ? "font-bold text-brand" : "font-bold text-gold"}>
                    {a.publie ? "Publié" : "Brouillon"}
                  </span>
                </div>
              </div>
              <button onClick={() => startEdit(a)} data-testid={`blog-edit-${a.slug}`} className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-brand-light hover:text-brand" aria-label={`Modifier ${a.titre}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(a)} data-testid={`blog-delete-${a.slug}`} className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-crimson" aria-label={`Supprimer ${a.titre}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- Équipe ---------------- */
const EquipeTab = ({ me }) => {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ nom: "", email: "", password: "", role: "collaborateur" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/users`, { headers: authHeaders() });
      setUsers(data);
    } catch {
      toast.error("Impossible de charger l'équipe");
    }
  }, []);

  useEffect(() => { if (me?.role === "admin") load(); }, [me, load]);

  if (me?.role !== "admin") {
    return (
      <div data-testid="equipe-forbidden" className="rounded-2xl border border-gold/30 bg-gold-glow/40 p-8 text-sm text-slate-700">
        La gestion des comptes est réservée aux administrateurs.
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/auth/users`, form, { headers: authHeaders() });
      toast.success(`Compte créé pour ${form.email} — communiquez-lui ses identifiants en main propre.`);
      setForm({ nom: "", email: "", password: "", role: "collaborateur" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (u) => {
    try {
      await axios.put(`${API}/auth/users/${u.id}`, { actif: !u.actif }, { headers: authHeaders() });
      toast.success(u.actif ? `Compte ${u.email} désactivé` : `Compte ${u.email} réactivé`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const resetPassword = async (u) => {
    const p = window.prompt(`Nouveau mot de passe pour ${u.email} (8 caractères min.) :`);
    if (!p) return;
    try {
      await axios.put(`${API}/auth/users/${u.id}`, { password: p }, { headers: authHeaders() });
      toast.success("Mot de passe réinitialisé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await axios.put(`${API}/auth/users/${u.id}`, { role }, { headers: authHeaders() });
      toast.success("Rôle mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-equipe-tab" className="grid grid-cols-1 gap-8 lg:grid-cols-5">
      <form onSubmit={create} data-testid="equipe-form" className="h-fit rounded-2xl border border-brand/10 bg-white p-7 shadow-sm lg:col-span-2">
        <h3 className="font-display text-lg font-bold text-slate-900">Créer un collaborateur</h3>
        <div className="mt-5 space-y-4">
          <div>
            <label className={labelCls} htmlFor="eq-nom">Nom complet</label>
            <input id="eq-nom" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="equipe-nom" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="eq-email">Email</label>
            <input id="eq-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="equipe-email" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="eq-password">Mot de passe (8 caractères min.)</label>
            <input id="eq-password" type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="equipe-password" className={inputCls} placeholder="À communiquer en main propre" />
          </div>
          <div>
            <label className={labelCls} htmlFor="eq-role">Rôle</label>
            <select id="eq-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="equipe-role" className={inputCls}>
              <option value="collaborateur">Collaborateur (demandes, blog, logo)</option>
              <option value="admin">Administrateur (+ gestion de l'équipe)</option>
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid="equipe-create-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Créer le compte
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        {users === null ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div>
        ) : (
          <div className="space-y-3" data-testid="equipe-list">
            {users.map((u) => (
              <div key={u.id} data-testid={`equipe-row-${u.email}`} className={`flex flex-wrap items-center gap-4 rounded-2xl border bg-white p-5 shadow-sm ${u.actif ? "border-brand/10" : "border-slate-200 opacity-60"}`}>
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-light font-display text-sm font-bold text-brand">
                  {(u.nom || u.email).slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display font-bold text-slate-900">{u.nom || "—"}</div>
                  <div className="truncate text-xs text-slate-400">{u.email}</div>
                </div>
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  data-testid={`equipe-role-${u.email}`}
                  className="rounded-lg border border-brand/15 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                >
                  <option value="collaborateur">Collaborateur</option>
                  <option value="admin">Administrateur</option>
                </select>
                <button onClick={() => resetPassword(u)} data-testid={`equipe-reset-${u.email}`} className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-gold-glow hover:text-gold" aria-label={`Réinitialiser le mot de passe de ${u.email}`}>
                  <KeyRound className="h-4 w-4" />
                </button>
                <button
                  onClick={() => toggleActif(u)}
                  data-testid={`equipe-toggle-${u.email}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${u.actif ? "bg-red-50 text-crimson hover:bg-red-100" : "bg-brand-light text-brand hover:bg-brand hover:text-white"}`}
                >
                  {u.actif ? "Désactiver" : "Réactiver"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Logo ---------------- */
const LogoTab = () => {
  const [current, setCurrent] = useState(undefined);
  const [currentFavicon, setCurrentFavicon] = useState(undefined);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [favFile, setFavFile] = useState(null);
  const [favPreview, setFavPreview] = useState(null);
  const [savingFav, setSavingFav] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings/branding`)
      .then((r) => {
        setCurrent(r.data.logo_url);
        setCurrentFavicon(r.data.favicon_url);
      })
      .catch(() => {
        setCurrent(null);
        setCurrentFavicon(null);
      });
  }, []);

  const pickFav = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFavFile(f);
    setFavPreview(URL.createObjectURL(f));
  };

  const saveFavicon = async () => {
    if (!favFile) return;
    setSavingFav(true);
    try {
      const fd = new FormData();
      fd.append("fichier", favFile);
      const { data } = await axios.post(`${API}/admin/settings/favicon`, fd, { headers: authHeaders() });
      setCurrentFavicon(data.favicon_url);
      setFavFile(null);
      setFavPreview(null);
      toast.success("Favicon mis à jour — visible dans l'onglet du navigateur");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingFav(false);
    }
  };

  const pick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("fichier", file);
      const { data } = await axios.post(`${API}/admin/settings/logo`, fd, { headers: authHeaders() });
      setCurrent(data.logo_url);
      setFile(null);
      setPreview(null);
      toast.success("Logo mis à jour — rechargez le site public pour le voir");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-logo-tab" className="max-w-xl space-y-6">
      <div className="rounded-2xl border border-brand/10 bg-white p-8 shadow-sm">
      <h3 className="font-display text-lg font-bold text-slate-900">Logo du site</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Le logo choisi ici remplace l'emblème par défaut dans l'en-tête et le pied de page du site.
        Formats acceptés : PNG, JPG, WebP, SVG (max 5 Mo). Un fond transparent carré donne le meilleur rendu.
      </p>
      <div className="mt-6 flex items-center gap-6">
        <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-2xl border border-brand/10 bg-brand-deep">
          {preview ? (
            <img src={preview} alt="Aperçu du nouveau logo" className="h-full w-full object-contain" data-testid="logo-preview-new" />
          ) : current ? (
            <img src={`${BACKEND}${current}`} alt="Logo actuel" className="h-full w-full object-contain" data-testid="logo-preview-current" />
          ) : (
            <span className="font-display text-2xl font-black text-gold" data-testid="logo-preview-default">D</span>
          )}
        </div>
        <div className="flex-1">
          <label data-testid="logo-upload-drop" className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-brand/25 bg-brand-light/40 px-4 py-4 text-sm text-slate-600 transition-colors hover:border-brand">
            <UploadCloud className="h-5 w-5 shrink-0 text-brand" />
            <span className="truncate">{file ? file.name : "Choisir un fichier logo"}</span>
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" data-testid="logo-upload-input" onChange={pick} />
          </label>
          <button
            onClick={save}
            disabled={!file || saving}
            data-testid="logo-save-btn"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            Appliquer ce logo
          </button>
        </div>
      </div>
      </div>
      <div className="rounded-2xl border border-brand/10 bg-white p-8 shadow-sm" data-testid="admin-favicon-card">
        <h3 className="font-display text-lg font-bold text-slate-900">Favicon du site</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          Le favicon est la petite icône affichée dans l'onglet du navigateur. Formats acceptés :
          PNG, ICO, SVG, WebP (max 2 Mo). Idéal : un carré 64×64 px ou 32×32 px.
        </p>
        <div className="mt-6 flex items-center gap-6">
          <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-brand/10 bg-brand-deep">
            {favPreview ? (
              <img src={favPreview} alt="Aperçu du nouveau favicon" className="h-full w-full object-contain" data-testid="favicon-preview-new" />
            ) : currentFavicon ? (
              <img src={`${BACKEND}${currentFavicon}`} alt="Favicon actuel" className="h-full w-full object-contain" data-testid="favicon-preview-current" />
            ) : (
              <span className="font-display text-lg font-black text-gold" data-testid="favicon-preview-default">D</span>
            )}
          </div>
          <div className="flex-1">
            <label data-testid="favicon-upload-drop" className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-brand/25 bg-brand-light/40 px-4 py-3.5 text-sm text-slate-600 transition-colors hover:border-brand">
              <UploadCloud className="h-5 w-5 shrink-0 text-brand" />
              <span className="truncate">{favFile ? favFile.name : "Choisir un fichier favicon"}</span>
              <input type="file" accept=".png,.ico,.svg,.webp,.jpg,.jpeg" className="hidden" data-testid="favicon-upload-input" onChange={pickFav} />
            </label>
            <button
              onClick={saveFavicon}
              disabled={!favFile || savingFav}
              data-testid="favicon-save-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingFav ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              Appliquer ce favicon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Application mobile ---------------- */
const AppTab = () => {
  const [stats, setStats] = useState(null);
  const [push, setPush] = useState({ titre: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/app/stats`, { headers: authHeaders() });
      setStats(data);
    } catch {
      toast.error("Impossible de charger les statistiques de l'application");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendPush = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/app/push`, push, { headers: authHeaders() });
      toast.success(`${data.envoyees} notification(s) envoyée(s) aux utilisateurs de l'application`);
      setPush({ titre: "", message: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const parsePlatform = (ua) => {
    if (/android/i.test(ua)) return "Android";
    if (/iphone|ipad/i.test(ua)) return "iOS";
    if (/windows/i.test(ua)) return "Windows";
    if (/mac os/i.test(ua)) return "macOS";
    if (/linux/i.test(ua)) return "Linux";
    return "Autre";
  };

  const fmtDateTime = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return "—";
    }
  };

  return (
    <div data-testid="admin-app-tab" className="space-y-8">
      {stats === null ? (
        <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {[
            { label: "Installations de l'application", value: stats.installs, testid: "app-stat-installs" },
            { label: "Ouvertures de l'application", value: stats.lancements, testid: "app-stat-lancements" },
            { label: "Abonnés aux notifications", value: stats.abonnes_push, testid: "app-stat-push" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-brand/10 bg-white p-6 shadow-sm">
              <div className="font-display text-3xl font-extrabold text-brand">{s.value}</div>
              <div className="mt-1 text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={sendPush} data-testid="push-form" className="rounded-2xl border border-brand/10 bg-white p-7 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900">Envoyer une notification push</h3>
        <p className="mt-1 text-sm text-slate-500">
          Reçue instantanément par tous les utilisateurs ayant installé l'application et activé les notifications.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="push-titre">Titre *</label>
            <input id="push-titre" required maxLength={80} value={push.titre} onChange={(e) => setPush({ ...push, titre: e.target.value })} data-testid="push-titre" placeholder="Ex : Votre passeport est prêt" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="push-message">Message *</label>
            <input id="push-message" required maxLength={240} value={push.message} onChange={(e) => setPush({ ...push, message: e.target.value })} data-testid="push-message" placeholder="Ex : Retrait disponible à notre bureau de Lomé" className={inputCls} />
          </div>
        </div>
        <button type="submit" disabled={sending} data-testid="push-send-btn" className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          Envoyer à tous les abonnés
        </button>
      </form>

      <div className="rounded-2xl border border-brand/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-brand/10 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Activité de l'application</h3>
          <button onClick={load} data-testid="app-stats-refresh" className="inline-flex items-center gap-2 text-xs font-semibold text-brand transition-colors hover:text-brand-hover">
            <RefreshCw className="h-3.5 w-3.5" /> Actualiser
          </button>
        </div>
        {!stats || stats.evenements.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400">
            Aucune installation pour le moment. Elles apparaîtront ici dès que des visiteurs installeront l'application Android depuis le bouton du site.
          </p>
        ) : (
          <table className="w-full text-left text-sm" data-testid="app-events-table">
            <thead>
              <tr className="border-b border-brand/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-3.5 font-bold">Date</th>
                <th className="px-6 py-3.5 font-bold">Événement</th>
                <th className="px-6 py-3.5 font-bold">Appareil</th>
                <th className="px-6 py-3.5 font-bold">Langue</th>
              </tr>
            </thead>
            <tbody>
              {stats.evenements.map((ev) => (
                <tr key={ev.id} className="border-b border-slate-50 last:border-0">
                  <td className="px-6 py-3.5 text-xs text-slate-500">{fmtDateTime(ev.created_at)}</td>
                  <td className="px-6 py-3.5">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ev.event === "install" ? "bg-brand-light text-brand" : "bg-gold-glow text-gold"}`}>
                      {ev.event === "install" ? "Installation" : "Ouverture"}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-xs text-slate-600">{parsePlatform(ev.platform)}</td>
                  <td className="px-6 py-3.5 text-xs text-slate-600">{ev.langue || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ---------------- Root ---------------- */
const TABS = [
  { id: "demandes", label: "Demandes", icon: Inbox },
  { id: "concours", label: "Événements & concours", icon: Trophy },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "equipe", label: "Équipe", icon: Users },
  { id: "logo", label: "Logo & favicon", icon: ImageIcon },
  { id: "app", label: "Application mobile", icon: Smartphone },
];

export default function Admin() {
  usePageTitle("Back office");
  const navigate = useNavigate();
  const [tab, setTab] = useState("demandes");
  const [me, setMe] = useState(getUser());
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then((r) => {
        setMe(r.data);
        setChecking(false);
      })
      .catch(() => {
        clearSession();
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const logout = () => {
    clearSession();
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-brand-deep">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <div data-testid="admin-page" className="flex min-h-screen bg-ivory">
      {/* Barre latérale (desktop) */}
      <aside data-testid="admin-sidebar" className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-brand-deep text-white lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gold font-display text-sm font-black text-brand-deep">D</span>
          <div>
            <div className="font-display text-sm font-extrabold">DOC TOGO</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Back office</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1.5 overflow-y-auto px-4 py-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold transition-colors ${
                tab === t.id ? "bg-white/10 text-gold" : "text-white/55 hover:bg-white/5 hover:text-white"
              }`}
            >
              <t.icon className="h-4.5 w-4.5 shrink-0" />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 rounded-xl bg-white/5 px-4 py-3">
            <div className="truncate text-sm font-bold">{me?.nom || me?.email}</div>
            <div className="text-xs text-white/45">{me?.role === "admin" ? "Administrateur" : "Collaborateur"}</div>
          </div>
          <Link to="/" data-testid="admin-view-site" className="mb-2 flex items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-xs font-semibold text-white/70 transition-colors hover:border-gold hover:text-gold">
            Voir le site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-crimson">
            <LogOut className="h-3.5 w-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Contenu */}
      <div className="min-w-0 flex-1">
        {/* Barre mobile */}
        <div className="flex items-center justify-between gap-3 bg-brand-deep px-4 py-3.5 text-white lg:hidden">
          <span className="font-display text-sm font-extrabold">DOC TOGO — Back office</span>
          <button onClick={logout} data-testid="admin-logout-btn-mobile" className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-crimson" aria-label="Déconnexion">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto border-b border-brand/10 bg-white px-4 py-3 lg:hidden">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-mobile-${t.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                tab === t.id ? "bg-brand text-white" : "bg-slate-100 text-slate-500"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <div className="px-4 py-8 lg:px-10 lg:py-10">
          <div className="mb-8 flex items-center gap-3">
            {activeTab && <activeTab.icon className="h-6 w-6 text-brand" />}
            <h1 className="font-display text-2xl font-extrabold text-slate-900">{activeTab?.label}</h1>
          </div>
          {tab === "demandes" && <DemandesTab />}
          {tab === "concours" && <ConcoursAdmin />}
          {tab === "blog" && <BlogTab />}
          {tab === "equipe" && <EquipeTab me={me} />}
          {tab === "logo" && <LogoTab />}
          {tab === "app" && <AppTab />}
        </div>
      </div>
    </div>
  );
}
